import React from "react";

import DotsHorizontal from "@uui/DotsHorizontal";
import DotsVertical from "@uui/DotsVertical";
import EditIcon from "@uui/Edit02";
import TrashIcon from "@uui/Trash02";
import RemoveIcon from "@uui/XSquare";

import { useMediaQuery } from "@mui/material";
import Divider from "@mui/material/Divider";

import TableCell from "@app/analytics/components/Table/TableCell";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import type { Report } from "@app/analytics/MeetingReports/types";
import { isTopicReport } from "@app/analytics/MeetingReports/utils";
import theme from "@app/analytics/theme";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import IconButton from "@rui/IconButton";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";

type MenuItemClickEventHandler = React.MouseEventHandler<HTMLLIElement>;

const ReportActionsCell = ({
  report,
  canEditReport = false,
  onDeleteReport,
  onRemoveReport,
  onRenameReport,
}: {
  report: Report;
  canEditReport?: boolean;
  onDeleteReport?: () => void;
  onRemoveReport: () => void;
  onRenameReport?: () => void;
}) => {
  const isSmallView = useMediaQuery(theme.breakpoints.down("sm"));
  const setEditReport = useReportsTable((state) => state.setEditReport);
  const { onClick, onClose, open, anchorEl } = useAnchorElement();

  const handleClickMenuItem: MenuItemClickEventHandler = (event) => {
    event.stopPropagation();
    onClose();
  };

  const handleClickDeleteReport: MenuItemClickEventHandler = (event) => {
    handleClickMenuItem(event);
    onDeleteReport?.();
  };

  const handleClickRemoveReport: MenuItemClickEventHandler = (event) => {
    handleClickMenuItem(event);
    onRemoveReport();
  };

  const handleClickRenameReport: MenuItemClickEventHandler = (event) => {
    handleClickMenuItem(event);
    if (isSmallView) {
      onRenameReport?.();
    } else {
      setEditReport({
        id: report.id,
        type: isTopicReport(report) ? "topic" : "session",
      });
    }
  };

  return (
    <>
      <TableCell
        sx={{
          width: { xxs: "auto", sm: "40px" },
          alignContent: { xxs: "start", sm: "center" },
        }}
      >
        <IconButton
          sx={{ paddingTop: { xxs: 1, sm: 1 } }}
          onClick={(e) => {
            e.stopPropagation();
            onClick(e);
          }}
        >
          {isSmallView ? (
            <DotsVertical sx={{ height: "16px", width: "16px" }} />
          ) : (
            <DotsHorizontal />
          )}
        </IconButton>
      </TableCell>

      <Menu open={open} anchorEl={anchorEl} onClose={onClose}>
        {canEditReport && (
          <MenuItem icon={<EditIcon />} onClick={handleClickRenameReport}>
            Rename report
          </MenuItem>
        )}
        {canEditReport && <Divider />}
        {onDeleteReport ? (
          <MenuItem
            icon={<TrashIcon />}
            onClick={handleClickDeleteReport}
            type="error"
          >
            Delete report
          </MenuItem>
        ) : (
          <MenuItem
            icon={<RemoveIcon />}
            onClick={handleClickRemoveReport}
            type="error"
          >
            Remove report
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ReportActionsCell;
